import React from 'react';
import styled from 'styled-components';

interface Props {
	onToggleInlineStyle: (style: string) => void;
	onToggleBlockType: (blockType: string) => void;
	onInsertLink: () => void;
	activeStyles: string[];
	activeBlockType: string;
}

const Toolbar = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	border-bottom: 1px solid ${p => p.theme.sys.color.border.decorative};
	padding: 8px 12px;
`;

const ToolbarBtn = styled.button<{$active: boolean}>`
	border: 0;
	border-radius: 4px;
	padding: 4px 8px;
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 500;
	cursor: pointer;
	transition: background-color 0.15s, color 0.15s;
	background: ${p => (p.$active ? p.theme.sys.color.border.decorative : 'transparent')};
	color: ${p => (p.$active ? p.theme.sys.color.text.content.main : p.theme.sys.color.text.content.auxiliary)};
	&:hover {
		background: ${p => (p.$active ? p.theme.sys.color.border.decorative : p.theme.sys.color.background.disabled['1'])};
	}
`;

interface ToolbarButton {
	label: string;
	ariaLabel: string;
	action: () => void;
	isActive: boolean;
}

export function EditorToolbar({onToggleInlineStyle, onToggleBlockType, onInsertLink, activeStyles, activeBlockType}: Props) {
	const buttons: ToolbarButton[] = [
		{
			label: 'B',
			ariaLabel: 'Bold',
			action: () => onToggleInlineStyle('BOLD'),
			isActive: activeStyles.includes('BOLD'),
		},
		{
			label: 'I',
			ariaLabel: 'Italic',
			action: () => onToggleInlineStyle('ITALIC'),
			isActive: activeStyles.includes('ITALIC'),
		},
		{
			label: 'U',
			ariaLabel: 'Underline',
			action: () => onToggleInlineStyle('UNDERLINE'),
			isActive: activeStyles.includes('UNDERLINE'),
		},
		{
			label: '🔗',
			ariaLabel: 'Link',
			action: onInsertLink,
			isActive: false,
		},
		{
			label: '•',
			ariaLabel: 'Bullet list',
			action: () => onToggleBlockType('unordered-list-item'),
			isActive: activeBlockType === 'unordered-list-item',
		},
		{
			label: '1.',
			ariaLabel: 'Numbered list',
			action: () => onToggleBlockType('ordered-list-item'),
			isActive: activeBlockType === 'ordered-list-item',
		},
	];

	return (
		<Toolbar role="toolbar" aria-label="Formatting toolbar">
			{buttons.map(btn => (
				<ToolbarBtn
					key={btn.ariaLabel}
					type="button"
					aria-label={btn.ariaLabel}
					$active={btn.isActive}
					onMouseDown={e => {
						e.preventDefault();
						btn.action();
					}}
				>
					{btn.label}
				</ToolbarBtn>
			))}
		</Toolbar>
	);
}
